
import { Component, Vue, Emit, Prop } from 'vue-property-decorator';
import {
  SupplierMigrationModel,
  MigrationPreview,
  JobStatus,
  MetafieldDefinition,
  MigrationConfig
} from '@/models/supplier_migration';
import { EventBus } from '@/eventBus';

@Component
export default class ShopifyMigrationWizard extends Vue {
  @Prop({ type: String, required: true })
  private initialSource!: 'vendor' | 'metafield';

  private step = 0;
  private loading = false;
  private error: string | null = null;
  private previewData: MigrationPreview | null = null;
  private jobStatus: JobStatus | null = null;
  private pollingInterval: number | null = null;
  private conflictResolution: 'skip' | 'update' = 'skip';

  // --- Metafield Specific Data ---
  private metafieldDefinitions: MetafieldDefinition[] = [];
  private mapping: Record<string, string> = {
    supplierName: '',
  };
  private targetSupplierFields = [
    { text: 'Supplier Name', value: 'supplierName', required: true },
  ];
  // -----------------------------

  private vendorTableHeaders = [
    { text: 'Vendor/Supplier Name', value: 'name' },
    { text: 'Linked product variants', value: 'productCount' },
  ];

  async mounted() {
    // Data fetching is triggered by user actions
  }

  async fetchPreviewData() {
    this.loading = true;
    this.error = null;
    try {
      if (this.initialSource === 'vendor') {
        this.previewData = await SupplierMigrationModel.getMigrationPreview('vendor');
      } else if (this.initialSource === 'metafield') {
        this.previewData = await SupplierMigrationModel.getMigrationPreview('metafield', this.mapping);
      }
    } catch (err: any) {
      this.error = err.message || 'An unknown error occurred while fetching the preview.';
    } finally {
      this.loading = false;
    }
  }

  async fetchMetafieldDefinitions() {
    this.loading = true;
    this.error = null;
    try {
      const response = await SupplierMigrationModel.getMetafieldDefinitions();
      this.metafieldDefinitions = response.metafieldDefinitions;
    } catch (err: any) {
      this.error = err.message || 'Could not load Shopify Metafield definitions.';
    } finally {
      this.loading = false;
    }
  }

  get wizardMaxWidth(): string {
    if (this.initialSource === 'metafield') {
      return this.step === 1 ? '800px' : '600px';
    }
    return this.step === 2 ? '800px' : '600px';
  }

  get currentTitle(): string {
    if (this.initialSource === 'vendor') {
      switch (this.step) {
        case 0:
          return 'Import from Shopify Vendors';
        case 1:
          return 'Import from Vendors: Preview';
        case 2:
          return 'Import from Vendors: Review';
        case 3:
          return 'Import from Vendors: In Progress';
        default:
          return 'Import from Vendors';
      }
    } else if (this.initialSource === 'metafield') {
      switch (this.step) {
        case 0:
          return 'Import from Shopify Metafields';
        case 1:
          return 'Import from Metafields: Map Fields';
        case 2:
          return 'Import from Metafields: Preview';
        case 3:
          return 'Import from Metafields: Review';
        case 4:
          return 'Importing Suppliers';
      }
    }
    return 'Supplier Import';
  }

  get isJobRunning(): boolean {
    return this.jobStatus !== null && (this.jobStatus.status === 'processing' || this.jobStatus.status === 'pending');
  }

  get isStartStep(): boolean {
    return this.step === 0;
  }

  get isMetafieldMappingValid(): boolean {
    return !!this.mapping.supplierName;
  }

  get nextButtonText(): string {
    if (this.initialSource === 'vendor') {
      return this.isJobRunning || this.loading ? 'Processing...' : 'Start Import';
    } else if (this.initialSource === 'metafield') {
      if (this.step === 1) return 'Preview';
      if (this.step === 2) return 'Review';
      if (this.step === 3) return 'Start Import';
    }
    return 'Next';
  }

  get isNextButtonDisabled(): boolean {
    if (this.loading || this.isJobRunning) return true;
    if (this.initialSource === 'metafield') {
      if (this.step === 1) {
        return !this.isMetafieldMappingValid;
      }
      if (this.step === 2) {
        return !this.previewData || this.previewData.vendors.length === 0;
      }
    }
    return false;
  }

  goBack() {
    if (this.step > 0) {
      this.step -= 1;
    }
  }

  async proceed() {
    this.error = null;
    // --- Vendor Path ---
    if (this.initialSource === 'vendor') {
      if (this.step === 0) {
        this.step = 1;
        await this.fetchPreviewData();
        return;
      }
      if (this.step === 1) {
        this.step = 2; // Move to review step
        return;
      }
      if (this.step === 2) {
        this.step = 3; // Move to progress step
        await this.startImport();
        return;
      }
    }
    // --- Metafield Path ---
    if (this.initialSource === 'metafield') {
      if (this.step === 0) {
        this.step = 1; // Move to mapping
        await this.fetchMetafieldDefinitions();
        return;
      }
      if (this.step === 1) {
        this.step = 2; // Move to preview
        await this.fetchPreviewData();
        return;
      }
      if (this.step === 2) {
        this.step = 3; // Move to review
        return;
      }
      if (this.step === 3) {
        this.step = 4; // Move to progress
        await this.startImport();
        return;
      }
    }
  }

  async startImport() {
    this.error = null;
    this.loading = true;

    try {
      const config: MigrationConfig = {
        source: this.initialSource as 'vendor' | 'metafield',
        conflictResolution: this.conflictResolution,
      };

      if (this.initialSource === 'metafield') {
        config.mapping = this.mapping;
      }

      const job = await SupplierMigrationModel.startMigration(config);
      this.jobStatus = { status: 'pending', message: 'Import job started...' };
      this.startPolling(job.jobId);
    } catch (error: any) {
      this.error = error.message || 'An unknown error occurred while starting the import.';
      this.loading = false;
    }
  }

  startPolling(jobId: string) {
    this.pollingInterval = window.setInterval(async () => {
      try {
        const status = await SupplierMigrationModel.getMigrationStatus(jobId);
        this.jobStatus = status;

        if (status.status === 'completed' || status.status === 'error') {
          if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
          }
        }
      } catch (err) {
        // Stop polling on error
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
        this.error = 'Failed to get import status.';
      }
    }, 2000); // Poll every 2 seconds
  }

  beforeDestroy() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }

  @Emit('close')
  closeDialog() {
    // This emit is handled by the template, but the method is here for clarity
  }

  reload() {
    EventBus.$emit('supplier-migration-complete');
    this.$emit('close');
  }
}
